import React, { useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import {
  CONSENT_AGREEMENT,
  FIRST_NAME_VALIDATION,
  isDomainBlocked,
  LAST_NAME_VALIDATION,
  WEBINAR_EMAIL_VALIDATION,
  WORK_EMAIL_VALIDATION,
  hasUTMConsent,
  createLegalConsentOptions,
} from "../../../utilities/helpers";
import {
  consentText,
  emailRegex,
  FDA_GUIDE_PDF_LINK,
  HUBSPOT_PROXY_URL,
  ROBOTICS_PDF_LINK,
} from "../../../constants";
import CustomModal from "../CustomModal";
import { useLocation } from "@reach/router";
import { trackFormSubmission } from "../../../utilities/formEventTracking";
import xss from "xss";
import { handleHubSpotError } from "../../../utilities/hubspotErrorHandler";

// const SingleEmailFormFormSchema = Yup.object().shape({
//   email: WORK_EMAIL_VALIDATION,
// });

const CtaBannerMunchForm = ({
  setEmail,
  border,
  buttonText,
  formHsClass = "commercial-form",
  portalId = "25381551",
  formId = "c90a5ae1-1852-43f5-9346-a57837b9f470",
  pagePath,
  heroStyle = false, // New prop to enable homepage-style layout
  redirectUrl = null, // New prop for redirect URL
}) => {
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setisSubmitted] = useState(false);
  const [error, setError] = useState("");
  const pdfLink = FDA_GUIDE_PDF_LINK;
  const [message, setMessage] = useState("");
  const Location = useLocation();
  const isIntelligentMachines =
    Location?.pathname == "/the-rise-of-intelligent-machines/";
  const modifiedLegalNotice = consentText.legalNotice.replace(
    "submit",
    buttonText,
  );
  const initialValues =
    formId === "636a9fb4-a883-41f8-aee0-e397ce335c8a" || pagePath
      ? {
          email: "",
          firstName: "",
          lastName: "",
          consent_to_communicate_checkbox: false,
        }
      : { email: "", consent_to_communicate_checkbox: false };

  const SingleEmailFormFormSchema =
    formId === "636a9fb4-a883-41f8-aee0-e397ce335c8a" || pagePath
      ? Yup.object().shape({
          email: WEBINAR_EMAIL_VALIDATION,
          firstName: FIRST_NAME_VALIDATION,
          lastName: LAST_NAME_VALIDATION,
          consent_to_communicate_checkbox: CONSENT_AGREEMENT,
        })
      : Yup.object().shape({
          email: WORK_EMAIL_VALIDATION,
          consent_to_communicate_checkbox: CONSENT_AGREEMENT,
        });

  const handleSubmit = async (values, { resetForm }) => {
    try {
      // Clear any previous errors
      setError("");

      const { email, firstName, lastName } = values;

      // Get HubSpot tracking cookie
      const [cookie] = document.cookie
        .split("; ")
        .filter((x) => x.includes("hubspotutk="));
      const [, hubspotutk] = cookie?.split("=") || ["", ""];

      // Extract UTM parameters
      let utmParams = {};
      if (hasUTMConsent()) {
        const urlParams = new URLSearchParams(window.location.search);
        utmParams = {
          utm_medium: urlParams.get("utm_medium") || "",
          utm_source: urlParams.get("utm_source") || "",
          utm_campaign: urlParams.get("utm_campaign") || "",
          utm_content: urlParams.get("utm_content") || "",
          utm_term: urlParams.get("utm_term") || "",
        };
      }

      if (
        emailRegex.test(email) &&
        (isIntelligentMachines || !isDomainBlocked(email))
      ) {
        setLoading(true);
        const hasConsent = values?.consent_to_communicate_checkbox;
        const requestPayload = {
          fields: [
            {
              objectTypeId: "0-1",
              name: "email",
              value: email,
            },
            {
              objectTypeId: "0-1",
              name: "consent_to_receive_communication_checkbox",
              value: values?.consent_to_communicate_checkbox,
            },
          ],
          ...createLegalConsentOptions(hasConsent),
          context: {
            pageUri: location?.href,
            pageName: location?.pathname,
          },
        };

        // Add UTM parameters for specific form ID
        if (
          formId === "f0cf2f52-d2e6-46fb-aeef-4d0ecc8ca70d" &&
          hasUTMConsent()
        ) {
          requestPayload.fields.push(
            ...Object.entries(utmParams).map(([key, value]) => ({
              objectTypeId: "0-1",
              name: key,
              value: value,
            })),
          );
        }

        if (formId === "636a9fb4-a883-41f8-aee0-e397ce335c8a" || pagePath) {
          requestPayload.fields.push(
            {
              objectTypeId: "0-1",
              name: "firstname",
              value: firstName,
            },
            {
              objectTypeId: "0-1",
              name: "lastname",
              value: lastName,
            },
            {
              objectTypeId: "0-1",
              name: "consent_to_receive_communication_checkbox",
              value: values?.consent_to_communicate_checkbox,
            },
          );
        }

        if (hubspotutk) {
          requestPayload.context.hutk = hubspotutk;
        }

        const response = await axios.post(
          HUBSPOT_PROXY_URL,
          Object.assign(requestPayload, {
            portalId,
            formId: formId || "f0cf2f52-d2e6-46fb-aeef-4d0ecc8ca70d",
          }),
        );
        // Add GTM DataLayer Push with UTM parameters
        if (
          formId === "f0cf2f52-d2e6-46fb-aeef-4d0ecc8ca70d" &&
          window.dataLayer
        ) {
          window.dataLayer.push({
            event: "form_submission",
            form_type: "lead_capture",
            form_id: formId,
            form_details: {
              email: email,
              firstName: firstName,
              lastName: lastName,
              ...utmParams,
            },
          });
        }
        // Track form submission using utility function
        trackFormSubmission(formId, email);

        if (redirectUrl) {
          setMessage(
            `<p>Thank you! 🎉</p><p class="mt-2">You will be redirected to the content hub shortly.</p>`,
          );
        } else {
          setMessage(
            response.data?.inlineMessage ||
              `<strong>You're In!</strong>! 🚀 <br/><br/>Your e-book download is ready!`,
          );
        }

        /**
         * Append custom properties to Heap
         */
        if (window.heap && hasUTMConsent()) {
          window.heap.addUserProperties({
            marketing_email: email,
          });
        }

        // Redirect to demo booking page for "Book a demo" button
        if (buttonText === "Book a demo") {
          window.location.href = `/demo-booking?email=${encodeURIComponent(email)}`;
          return;
        }

        setEmail && setEmail(email);
        // if (formId === "f0cf2f52-d2e6-46fb-aeef-4d0ecc8ca70d") {
        //   downloadFile();
        // }
        setisSubmitted(true);
        resetForm();
      } else {
        setError("Please enter your work email to continue");
      }
    } catch (error) {
      const errorMessage = handleHubSpotError(error);
      console.error("Form submission error:", errorMessage);
    } finally {
      setLoading(false);
    }
  };
  //Download the pdf
  const downloadFile = () => {
    axios({
      url: ROBOTICS_PDF_LINK,
      responseType: "blob",
      method: "GET",
    })
      .then((response) => {
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `The Rise of Intelligent Machines.pdf`);

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode.removeChild(link);
      })
      .catch((err) => {
        console.error("Error while file download:", err);
      });
  };
  const closeSuccessModal = () => {
    setisSubmitted(false);
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={SingleEmailFormFormSchema}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          values,
          setTouched,
          validateForm,
          setFieldValue,
          form,
          isValid,
          dirty,
          ...rest
        }) => {
          return (
            <section
              className={`flex ${pagePath && "lg:pb-10"} flex-col w-full`}
            >
              <Form
                className={`w-full relative gap-3 ${formHsClass || "commercial-form"}  sm:flex-row`}
              >
                {(formId === "636a9fb4-a883-41f8-aee0-e397ce335c8a" ||
                  pagePath) &&
                  !heroStyle && (
                    <div className="flex flex-row my-3 gap-x-3 sm:flex-row gap-y-2 sm:gap-y-0">
                      <div className="flex flex-col w-full">
                        <Field
                          name="firstName"
                          placeholder="Enter your first name"
                          type="text"
                          className={`w-full  text-gray-2500 border-gray-1400 placeholder-gray-600 focus:rounded-4xl rounded-4xl focus:!shadow-none focus:!outline-none focus:ring-0  placeholder:text-base placeholder:tracking-[-0.8px] sm:placeholder:text-start ${pagePath ? "placeholder:text-start" : "placeholder:text-center "}  leading-6 disabled:bg-gray-300 bg-white sm:pl-[18px] py-3 sm:py-3.5 pr-1.5 ring-0`}
                        />
                        {errors.firstName && touched.firstName ? (
                          <small className="pt-2 pl-5 text-red-500 ">
                            {errors.firstName}
                          </small>
                        ) : null}
                      </div>

                      <div className="flex flex-col w-full">
                        <Field
                          name="lastName"
                          placeholder="Enter your last name"
                          type="text"
                          className={`w-full  text-gray-2500 border-gray-1400 placeholder-gray-600 focus:rounded-4xl rounded-4xl focus:!shadow-none focus:!outline-none focus:ring-0  placeholder:text-base placeholder:tracking-[-0.8px] sm:placeholder:text-start ${pagePath ? "placeholder:text-start" : "placeholder:text-center "}  leading-6 disabled:bg-gray-300 bg-white sm:pl-[18px] py-3 sm:py-3.5 pr-1.5 ring-0`}
                        />
                        {errors.lastName && touched.lastName ? (
                          <small className="pt-2 pl-5 text-red-500 ">
                            {errors.lastName}
                          </small>
                        ) : null}
                      </div>
                    </div>
                  )}

                {heroStyle ? (
                  // Homepage-style layout
                  <div className="flex gap-2 flex-1 relative">
                    <Field
                      name="email"
                      placeholder="What's your work email?"
                      type="email"
                      className="flex-1 text-gray-2500 placeholder-gray-2200 placeholder:text-base placeholder:tracking-[-0.8px] rounded-full leading-6 border border-gray-300 disabled:bg-gray-300 bg-white pl-[22px] pr-[22px] py-3 focus:outline-none focus:ring-2 focus:ring-purple-1500 focus:border-purple-1500 shadow-sm transition-all"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      onClick={() => {
                        if (!dirty) {
                          setTouched({ email: true });
                        }
                      }}
                      className="disabled:cursor-not-allowed flex gap-1 items-center px-6 py-3 font-manrope tracking-[-0.4px] text-white bg-purple-1500 hover:bg-purple-1800 transition-colors shrink-0 rounded-full font-semibold whitespace-nowrap"
                    >
                      <span className="hidden sm:inline-block">
                        {buttonText}
                      </span>
                      <svg
                        className="block sm:hidden"
                        width="24"
                        height="24"
                        role="presentation"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20.3672 11.4328L11.5125 3.74766C11.4445 3.68906 11.3578 3.65625 11.2664 3.65625H9.19219C9.01875 3.65625 8.93906 3.87187 9.07031 3.98438L17.2781 11.1094H3.5625C3.45937 11.1094 3.375 11.1937 3.375 11.2969V12.7031C3.375 12.8062 3.45937 12.8906 3.5625 12.8906H17.2758L9.06797 20.0156C8.93672 20.1305 9.01641 20.3438 9.18984 20.3438H11.3344C11.3789 20.3438 11.4234 20.3273 11.4562 20.2969L20.3672 12.5672C20.4483 12.4966 20.5134 12.4095 20.558 12.3116C20.6025 12.2138 20.6256 12.1075 20.6256 12C20.6256 11.8925 20.6025 11.7862 20.558 11.6884C20.5134 11.5905 20.4483 11.5034 20.3672 11.4328Z"
                          fill="white"
                        />
                      </svg>
                      {loading && (
                        <svg
                          className="w-5 h-5 text-white animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          role="presentation"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      )}
                    </button>
                  </div>
                ) : (
                  // Original layout
                  <div className="relative">
                    <div
                      className={`cta_border_animation ${border ? "border-none" : "border"} !rounded-4xl  p-[1px]`}
                    >
                      <Field
                        name="email"
                        placeholder="What's your work email?"
                        type="email"
                        className={`w-full sm:min-w-[460px] border-none text-gray-2500 placeholder-gray-600 focus:rounded-4xl rounded-4xl focus:!shadow-none focus:!outline-none focus:ring-0 focus:!border-none placeholder:text-base placeholder:tracking-[-0.8px] sm:placeholder:text-start ${pagePath ? "placeholder:text-start" : "placeholder:text-center "}  leading-6 disabled:bg-gray-300 bg-white sm:pl-[18px] py-3 sm:py-3.5 ring-0`}
                      />
                    </div>

                    <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
                      <div
                        className="hs-field-desc"
                        style={{ display: "none" }}
                      ></div>
                      <div className="cta-actions">
                        <button
                          aria-label="submit"
                          type="submit"
                          disabled={loading}
                          id="hero-book-demo-submit-btn-banner"
                          onClick={() => {
                            if (!dirty) {
                              setTouched({ email: true });
                            }
                          }}
                          className="disabled:cursor-not-allowed hidden sm:flex gap-1 items-center px-4 py-2 w-full font-manrope tracking-[-0.4px] text-white bg-purple-1500 shrink-0 rounded-4xl font-semibold"
                        >
                          <span className="hidden sm:inline-block">
                            {buttonText}
                          </span>
                          {loading && (
                            <svg
                              className="w-5 h-5 text-white animate-spin"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              role="presentation"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <button
                      aria-label="submit"
                      type="submit"
                      disabled={loading}
                      id="hero-book-demo-submit-btn-banner-mobile"
                      onClick={() => {
                        if (!dirty) {
                          setTouched({ email: true });
                        }
                      }}
                      className={`disabled:cursor-not-allowed mt-3 flex sm:hidden gap-1 items-center px-4 py-3 sm:py-2 text-center ${pagePath ? "max-w-full sm:max-w-fit w-full" : "w-full"}  mx-auto font-manrope tracking-[-0.4px] text-white bg-purple-1500 shrink-0 rounded-4xl font-semibold`}
                    >
                      <span className="inline-block mx-auto text-center ">
                        {buttonText}
                      </span>
                      {loading && (
                        <svg
                          className="w-5 h-5 text-white animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          role="presentation"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      )}
                    </button>
                  </div>
                )}
                {errors.email && touched.email ? (
                  <small className="pt-2 pl-5 text-red-500 ">
                    {errors.email}
                  </small>
                ) : error ? (
                  <ul className="pt-1 text-left text-red-500 mt-2" role="alert">
                    <li>
                      <label>{error}</label>
                    </li>
                  </ul>
                ) : null}
                <div className="mt-4 gap-2 flex w-full flex-col">
                  <label className="flex gap-2 text-xs consent-required-histerik text-gray-1200 font-normal items-start">
                    <Field
                      className="mt-[1px] appearance-none w-4 h-4 border cursor-pointer border-gray-300 checked:bg-purple-1500 checked:border-none "
                      type="checkbox"
                      name="consent_to_communicate_checkbox"
                      // required
                    />
                    <div
                      className="cursor-pointer"
                      dangerouslySetInnerHTML={{
                        __html: consentText?.marketingConsent,
                      }}
                    />
                  </label>
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: xss(modifiedLegalNotice),
                  }}
                  className="text-xs text-gray-1200 font-normal consent-anchor-tag mt-2.5"
                />
              </Form>
            </section>
          );
        }}
      </Formik>
      <CustomModal
        message={message}
        isSuccess={isSubmitted}
        closeSuccess={closeSuccessModal}
        redirectUrl={redirectUrl}
      />
    </>
  );
};

export default CtaBannerMunchForm;
